import { useInjection } from 'inversify-react';
import { useCallback, useEffect } from 'react';

import { Button, Panel } from '@desygner/ui-common-components';
import { useTranslation } from '@desygner/ui-common-translation';

import { VideoManager } from '../../../../../core/viewer/video-manager';
import { VirtualizerService } from '../../../../../core/viewer/virtualizer-service';
import { TYPES } from '../../../../../inversify.types';
import { IViewerStoreInjector } from '../../../../../store';
import { calculateZoomForFitToView } from '../../../../../utils/zoom.util';
import { StyledButtonSize } from '../BottomNav.styles';

export const FullScreenMode = () => {
  const useViewerStore = useInjection<IViewerStoreInjector>(TYPES.Store.Viewer);
  const isSinglePageMode = useViewerStore((state) => state.pageMode === 'single');
  const { t } = useTranslation();
  const pageDimension = useViewerStore((state) => state.pageDimension);
  const setZoom = useViewerStore((state) => state.setZoom);
  const setPageMode = useViewerStore((state) => state.setPageMode);
  const setViewOptions = useViewerStore((state) => state.setViewOptions);
  const setSelectedPageNumber = useViewerStore((state) => state.setSelectedPageNumber);

  const virtualizerService = useInjection<VirtualizerService>(TYPES.Viewer.VirtualizerService);
  const videoManager = useInjection<VideoManager>(TYPES.Viewer.VideoManager);

  const toggleFullScreenMode = useCallback(() => {
    videoManager.pauseCurrentActiveVideo();

    if (document.fullscreenElement) {
      setPageMode('scroll');
      document.exitFullscreen();
      return;
    }

    const currentSelectedPageNumber = useViewerStore.getState().selectedPageNumber;

    setViewOptions(null);
    setPageMode('single');
    setSelectedPageNumber(currentSelectedPageNumber);

    document.documentElement.requestFullscreen();

    const zoom = calculateZoomForFitToView(pageDimension);

    setZoom(zoom);
  }, [pageDimension, setPageMode, setSelectedPageNumber, setViewOptions, setZoom, videoManager]);

  const fullScreenChangeHandler = useCallback(() => {
    const shouldBeSinglePageMode = document.fullscreenElement;

    setPageMode(shouldBeSinglePageMode ? 'single' : 'scroll');

    (function keepCurrentPageInView() {
      const currentSelectedPageNumber = useViewerStore.getState().selectedPageNumber;

      virtualizerService.scrollToPage(currentSelectedPageNumber);
    })();
  }, [setPageMode, virtualizerService]);

  useEffect(() => {
    document.addEventListener('fullscreenchange', fullScreenChangeHandler);

    return () => {
      document.removeEventListener('fullscreenchange', fullScreenChangeHandler);
    };
  }, [fullScreenChangeHandler]);

  return (
    <StyledButtonSize>
      <Panel>
        <Panel.Content>
          <Button
            icon={{
              name: isSinglePageMode ? 'arrows-shrink' : 'arrows-expand',
            }}
            size="Small"
            variant="ghost"
            onClick={toggleFullScreenMode}
            label={t('tool.bottombar.full_screen_button')}
          />
        </Panel.Content>
      </Panel>
    </StyledButtonSize>
  );
};
